import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { makeStyles, useTheme } from '@material-ui/core'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { styles } from './priceComponentsTable.styles'

import { EnumCommodity } from '../../../api'
import { HttpErrorProps, useConfirmationDialog, useFormDialog, usePageState } from '../../../hooks'
import { GlobalNotification, NonAuthRoutes } from '../../../model'

import { getCustomerDomain, storeDataTableRowsPerPageForCurrentPage } from '../../../redux'
import { DataTableErrorHandler } from '../../errors/scopes/dataTable/dataTableError'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useHistory } from 'react-router-dom'
import { PriceComponentTable } from '../../../api/Argentia/endpoints/priceComponents'
import { enumEnumToValueNumber, enumToSelectOptionArray } from '../../../helpers'

import { AttachMoney, Delete } from '@material-ui/icons'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faMoneyCheckDollar } from '@fortawesome/free-solid-svg-icons'
import { EnumPriceComponentReference } from '../../../api/Argentia/enums/enumPriceComponentReference'
import { EnumPriceType } from '../../../api/oData/enums/enumPriceType'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { deletePriceComponent, updatePriceComponentValue } from './priceComponentsTable.service'

export interface PriceComponentsTableProps {
  priceComponents?: PriceComponentTable[]
  errorResponse?: HttpErrorProps | null
  onNotificationChange: (notificacion: GlobalNotification | undefined) => void
  onRefreshPriceComponents?: () => void
  cardMode?: boolean
}

export const PriceComponentsTable: React.FC<PriceComponentsTableProps> = ({
  priceComponents,
  onNotificationChange,
  onRefreshPriceComponents,
  errorResponse,
  cardMode = false
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'priceComponent', 'enums'])
  const history = useHistory()

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { info, error } = useNotify()
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { dataTableRowsPerPage } = usePageState()

  const onViewPriceVersionActionHandler = async (priceComponent: PriceComponentTable) => {
    history.push(NonAuthRoutes.pricingEdit, {
      priceId: priceComponent.idPrice,
      commodity: enumEnumToValueNumber(EnumCommodity, priceComponent.commodity),
      priceType: enumEnumToValueNumber(EnumPriceType, priceComponent.priceType),
      priceComponentId: priceComponent.id
    })
  }

  const onViewPriceComponentActionHandler = async (priceComponent: PriceComponentTable) => {
    history.push(NonAuthRoutes.PriceComponentsAdd, {
      id: priceComponent.id,
      priceComponentTypeID: priceComponent.idPriceComponentType,
      priceType: enumEnumToValueNumber(EnumPriceType, priceComponent.priceType),
      ...(enumEnumToValueNumber(EnumPriceType, priceComponent.priceType) ===
        EnumPriceType.ParametroPeriodo && {
        parametroPeriodoPriceID: priceComponent.idPrice
      }),
      priceComponentTypeReference: priceComponent.priceReference,
      leadID: priceComponent.idLead,
      flexibilityId: priceComponent.idFlexibility,
      contractId: priceComponent.idContract,
      commodity: priceComponent.commodity
    })
  }

  const editPriceValue = async (priceComponentID: number, newValue: number) => {
    setFormWaiting(true)
    await updatePriceComponentValue(priceComponentID, newValue)
      .then(response => {
        if (response.success) {
          onRefreshPriceComponents && onRefreshPriceComponents()
          info(t('priceComponent:dataTable.notifications.updateValue.success'))
        } else {
          error(t('priceComponent:dataTable.notifications.updateValue.error'))
        }
      })
      .finally(() => setFormWaiting(false))
    closeForm()
  }

  const onDeleteActionHandler = async (id: number) => {
    await getConfirmation(ConfirmationDialogs.DELETE_PRICECOMPONENT).then(async confirm => {
      await deletePriceComponent(id).then(response => {
        if (response.success) {
          onRefreshPriceComponents && onRefreshPriceComponents()
          info(t('priceComponent:dataTable.notifications.delete.success'))
        } else {
          error(t('priceComponent:dataTable.notifications.delete.error'))
        }
      })
    })
    closeConfirmation()
  }

  const dataTableColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'typeCode',
      label: t('priceComponent:dataTable.columns.code'),
      type: DataType.TEXT,
      columnWidth: 120
    },
    {
      name: 'typeDescription',
      label: t('priceComponent:dataTable.columns.description'),
      type: DataType.TEXT,
      columnWidth: 200
      // display: !clientID
    },
    {
      name: 'priceType',
      label: t('priceComponent:dataTable.columns.priceType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumPriceType, 'enums:EnumPriceTypes'),
      columnWidth: 120
    },
    {
      name: 'priceReference',
      label: t('priceComponent:dataTable.columns.linkedTo'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumPriceComponentReference,
        'enums:EnumPriceComponentReference'
      )
    },
    {
      name: 'currentPriceValueString',
      label: t('priceComponent:dataTable.columns.linkedValue'),
      type: DataType.ARRAY,
      columnWidth: 300
    }
  ]

  const dataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: !cardMode,
    elevation: 0,
    filterType: !cardMode ? FilterType.ADVANCED : undefined,
    download: !cardMode,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    selectableRows: false,
    customInsertEditModal: true,
    rowsPerPage: dataTableRowsPerPage || 5,
    rowsPerPageOptions: [5, 10, 20],
    analytics: {
      customer: getCustomerDomain(),
      view: t('priceComponent:dataTable.analytics')
    },
    rowCustomActions: [
      {
        userPermission: UserPermission.EDIT,
        icon: <AttachMoney fontSize="small" />,
        tooltip: t('priceComponent:dataTable.customActions.viewVersion.tooltip'),
        onClick: onViewPriceVersionActionHandler,
        visibleIf: 'isParameter'
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <FontAwesomeIcon size="sm" icon={faMoneyCheckDollar as IconProp} />,
        tooltip: t('priceComponent:dataTable.customActions.viewComponent.tooltip'),
        onClick: onViewPriceComponentActionHandler
      },
      // Hidden for now - It will be used through view price component page
      // {
      //   userPermission: UserPermission.EDIT,
      //   icon: <Edit fontSize="small" />,
      //   tooltip: t('priceComponent:dataTable.customActions.editValue.tooltip'),
      //   visibleIf: 'isParameter',
      //   onClick: (row: PriceComponentTable) => {
      //     row && onEditPriceValueActionHandler(row)
      //   }
      // },
      {
        userPermission: UserPermission.EDIT,
        icon: <Delete fontSize="small" />,
        tooltip: t('priceComponent:dataTable.customActions.deletePriceComponent.tooltip'),
        onClick: (row: PriceComponentTable) => onDeleteActionHandler(row.id)
      }
    ],
    onRowsPerPageChange: (rowsPerPage: number) => {
      storeDataTableRowsPerPageForCurrentPage(rowsPerPage)
    }
  }

  return (
    <>
      <DataTable
        data={priceComponents}
        columns={dataTableColumns}
        options={dataTableOptions}
        info={DataTableErrorHandler(errorResponse)}
      />
      <CustomFormDialog maxWidth="xs" {...formProps} />
    </>
  )
}
